import type { Block } from '../api/client.ts';
import { t, type StringKey } from '../i18n/index.ts';
import { formatClock } from '../lib/format.ts';
import { BlockThumbnail } from './BlockThumbnail.tsx';

const STATUS_LABEL: Record<Block['status'], StringKey> = {
  ready: 'block.ready',
  recording: 'block.recording',
  partial: 'block.partial',
  missing: 'block.missing',
  expired: 'block.expired',
};

/** A block this close to deletion is worth warning about (spec §6). */
const EXPIRY_WARNING_MS = 15 * 60_000;

interface Props {
  block: Block;
  timeZone: string;
  now: number;
  onEdit: (block: Block) => void;
  /** Identified by block start: a segment can appear in two blocks at once. */
  editingBlockStart: string | null;
}

export function BlockRow({
  block, timeZone, now, onEdit, editingBlockStart,
}: Props): React.ReactElement {
  const absent = block.status === 'missing' || block.status === 'expired';
  const playable = block.segments.length > 0;
  const msToExpiry = new Date(block.expires_at).getTime() - now;
  const expiringSoon = !absent && msToExpiry < EXPIRY_WARNING_MS;
  const isOpen = editingBlockStart === block.block_start_at;

  return (
    <div
      className={[
        'block',
        absent ? 'absent' : '',
        block.status === 'recording' ? 'is-recording' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* A time range reads left to right even on an RTL page. */}
      <div className="block-time">
        {formatClock(block.block_start_at, timeZone)}
        <span className="dash">–</span>
        {formatClock(block.block_end_at, timeZone)}
      </div>

      {/* Labelled only when something is out of the ordinary. A finished half
          hour is the normal case, and a label on every row said nothing. The
          slot stays so the columns line up either way. */}
      <span className="block-status">
        {block.status !== 'ready' && (
          <span className={`chip ${block.status}`}>{t(STATUS_LABEL[block.status])}</span>
        )}
      </span>

      <div className="block-meta">
        {/* The shape of the audio: speech, silence and any hole in the
            recording, all at a glance. */}
        {!absent && <BlockThumbnail block={block} />}

        {block.has_gap && block.status !== 'recording' && (
          <span className="block-note warn">{t('block.gap_warning')}</span>
        )}

        {/* Deletion is only worth mentioning once it is close. */}
        {expiringSoon && (
          <span className="block-note warn">{t('block.expires_soon')}</span>
        )}
      </div>

      <div className="block-actions">
        {/* The one thing to do with a half hour: open it, hear it, cut from
            it. design.md §5 reserves terracotta for CTAs. */}
        {playable && (
          <button
            type="button"
            className="cta"
            onClick={() => onEdit(block)}
            aria-pressed={isOpen}
          >
            {isOpen ? t('action.close') : t('action.listen_and_cut')}
          </button>
        )}
      </div>
    </div>
  );
}
